import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type DriveInfo,
  type JfConfigBody,
  type JfConfigResponse,
  type JfSchematicSuggestion,
} from "@tbm/shared";
import { api } from "../../lib/api";
import {
  loadDrawingFromUrl,
  payloadToPngBase64,
} from "../Drawing/loadDrawing";
import { LevelsEditor, type LevelRow } from "./LevelsEditor";
import { SoilsEditor, type SoilRowDraft } from "./SoilsEditor";
import { SchematicPreview } from "./SchematicPreview";

// Fallbacks for auto-filled coefficients when the model omits one of the
// three. Centred between the four reference bands the prompt instructs
// it to interpolate from, so the user gets something sane to edit
// rather than a blank.
const FALLBACK_SF_SS_KPA = 0.66;  // sands band
const FALLBACK_C = 1.44;          // sands band
const FALLBACK_A = 3.4;           // sands band

const APPROX = (a: number, b: number) => Math.abs(a - b) <= 1e-6;

interface SetupDrawerProps {
  open: boolean;
  onClose: () => void;
  drive: DriveInfo | null | undefined;
  initial: JfConfigResponse | null | undefined;
  /** True while the save is being persisted (DB write — fast). */
  isSaving: boolean;
  /** True while the sidecar is computing the prediction (NN — slow, 10-30s). */
  isComputing: boolean;
  error: string | null;
  onSave: (body: JfConfigBody) => void;
}

export function SetupDrawer({
  open,
  onClose,
  drive,
  initial,
  isSaving,
  isComputing,
  error,
  onSave,
}: SetupDrawerProps) {
  const driveLengthM = drive?.lengthM ?? null;
  const pipeOdMm = drive?.pipeOdMm ?? null;
  const pipeIdMm = drive?.pipeIdMm ?? null;

  // Local form state. Re-seed when the loaded config changes (e.g. after save).
  const [transitionWindowM, setTransitionWindowM] = useState<number | "">(50);
  const [customStoppageHours, setCustomStoppageHours] = useState<number | "">(
    "",
  );
  const [levels, setLevels] = useState<LevelRow[]>([
    { chainageM: 0, groundM: "", waterM: "", tunnelM: "" },
    { chainageM: driveLengthM ?? "", groundM: "", waterM: "", tunnelM: "" },
  ]);
  const [soils, setSoils] = useState<SoilRowDraft[]>([
    {
      soilType: "",
      unitWeightKnm3: "",
      startM: 0,
      endM: driveLengthM ?? "",
      sfSsKpaInit: "",
      cInit: "",
      aInit: "",
    },
  ]);

  // Reseed only when the *initial* row identity changes (saved -> loaded).
  // Never reseed mid-edit on every render; that would wipe user typing.
  useEffect(() => {
    if (!initial?.config) return;
    const c = initial.config;
    setTransitionWindowM(c.transitionWindowM);
    setCustomStoppageHours(c.customStoppageHours ?? "");
    setLevels(
      c.levelChainagesM.map((ch, i) => ({
        chainageM: ch,
        groundM: c.groundLevelsM[i] ?? "",
        waterM: c.waterLevelsM[i] ?? "",
        tunnelM: c.tunnelLevelsM[i] ?? "",
      })),
    );
    setSoils(
      initial.soils.map((s) => ({
        soilType: s.soilType,
        unitWeightKnm3: s.unitWeightKnm3,
        startM: s.startM,
        endM: s.endM,
        sfSsKpaInit: s.sfSsKpaInit,
        cInit: s.cInit,
        aInit: s.aInit,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.config?.updatedAt]);

  const validation = useMemo(
    () =>
      validate(
        levels,
        soils,
        transitionWindowM,
        customStoppageHours,
        driveLengthM,
      ),
    [levels, soils, transitionWindowM, driveLengthM],
  );

  const handleSave = () => {
    if (!validation.ok) return;
    onSave(validation.body);
  };

  // Auto-fill from the uploaded drawing. We need to know whether a
  // drawing exists (to disable the button) and to fetch + rasterise it
  // when the user clicks. The query is cheap (just the metadata row).
  const drawingMeta = useQuery({
    queryKey: ["drawing"],
    queryFn: api.drawing,
    enabled: open,
  });

  const autoFillMutation = useMutation({
    mutationFn: async () => {
      const meta = drawingMeta.data;
      if (!meta?.hasDrawing || !meta.mime) {
        throw new Error("Upload a drawing on the dashboard first");
      }
      const url = `/api/drawing/image?v=${encodeURIComponent(meta.updatedAt ?? "")}`;
      const payload = await loadDrawingFromUrl(url, meta.mime);
      const png = await payloadToPngBase64(payload);
      return api.autoAnalyzeDrawing({ png });
    },
    onSuccess: (resp) => {
      if (resp.jfSchematic) {
        applyJfSchematic(resp.jfSchematic, {
          setLevels,
          setSoils,
        });
      }
    },
  });

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[760px] max-w-[100vw] max-lg:w-full z-50 bg-surface1 border-l border-border1",
          "transform transition-transform duration-200 flex flex-col",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1 flex-shrink-0 gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
              Jacking force
            </div>
            <h2 className="text-[15px] font-semibold text-text">Drive setup</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => autoFillMutation.mutate()}
              disabled={
                !drawingMeta.data?.hasDrawing || autoFillMutation.isPending
              }
              title={
                drawingMeta.data?.hasDrawing
                  ? "Use Claude vision to pre-fill levels and soil layers from the uploaded drawing"
                  : "Upload a drawing on the dashboard first"
              }
              className={clsx(
                "text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border",
                drawingMeta.data?.hasDrawing && !autoFillMutation.isPending
                  ? "border-border2 text-text hover:border-wb hover:text-wb"
                  : "border-border1 text-dim cursor-not-allowed",
              )}
            >
              {autoFillMutation.isPending
                ? "Reading drawing…"
                : "Auto-fill from drawing"}
            </button>
            <button
              onClick={onClose}
              className="text-muted hover:text-text text-lg"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          <div className="flex items-center gap-2 text-[11px] text-muted">
            <Chip
              label="Drive length"
              value={driveLengthM != null ? `${driveLengthM} m` : "not set"}
              tone={driveLengthM != null ? "ok" : "warn"}
            />
            <Chip
              label="Pipe OD"
              value={pipeOdMm != null ? `${pipeOdMm} mm` : "not set"}
              tone={pipeOdMm != null ? "ok" : "warn"}
            />
            <Chip
              label="Pipe ID"
              value={pipeIdMm != null ? `${pipeIdMm} mm` : "not set"}
              tone={pipeIdMm != null ? "ok" : "warn"}
            />
          </div>

          {driveLengthM == null && (
            <div className="text-[11.5px] text-warn">
              Set the drive length on the Lubrication tab (Pipe schedule) first.
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="text-[11px] uppercase tracking-wider text-dim">
              Transition window (m)
            </label>
            <input
              type="number"
              step="any"
              value={transitionWindowM}
              onChange={(e) =>
                setTransitionWindowM(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="bg-bg border border-border1 rounded px-2 py-1 text-[12px] text-text w-16 focus:outline-none focus:border-wb"
            />
            <span className="text-[11px] text-muted">
              Distance over which live data per soil overrides the initial values.
            </span>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-[11px] uppercase tracking-wider text-dim">
              Custom stoppage (h)
            </label>
            <input
              type="number"
              step="any"
              min={0}
              value={customStoppageHours}
              placeholder="—"
              onChange={(e) =>
                setCustomStoppageHours(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="bg-bg border border-border1 rounded px-2 py-1 text-[12px] text-text w-16 focus:outline-none focus:border-wb"
            />
            <span className="text-[11px] text-muted">
              Optional extra stoppage trace alongside the 0 h and 12 h scenarios. Leave blank for none.
            </span>
          </div>

          <SchematicPreview
            levels={levels}
            soils={soils}
            driveLengthM={driveLengthM}
          />

          <div>
            <div className="text-[11px] uppercase tracking-wider text-dim mb-2">
              Levels along chainage
            </div>
            <LevelsEditor
              rows={levels}
              driveLengthM={driveLengthM}
              onChange={setLevels}
            />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-dim mb-2 flex items-center justify-between">
              <span>Soil layers</span>
              {autoFillMutation.data && (
                <span className="normal-case tracking-normal text-[10.5px] text-muted">
                  Auto-filled — review and adjust before saving
                </span>
              )}
            </div>
            <SoilsEditor
              rows={soils}
              driveLengthM={driveLengthM}
              onChange={setSoils}
            />
          </div>

          {autoFillMutation.error && (
            <div className="text-[11.5px] text-wb">
              Auto-fill failed: {(autoFillMutation.error as Error).message}
            </div>
          )}
          {autoFillMutation.data &&
            autoFillMutation.data.warnings.length > 0 && (
              <div className="text-[11.5px] text-warn whitespace-pre-line">
                {autoFillMutation.data.warnings.join("\n")}
              </div>
            )}

          {!validation.ok && (
            <div className="text-[11.5px] text-warn whitespace-pre-line">
              {validation.errors.join("\n")}
            </div>
          )}
          {error && <div className="text-[11.5px] text-wb">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 h-16 border-t border-border1 flex-shrink-0">
          <button
            type="button"
            disabled={!validation.ok || isSaving || isComputing}
            onClick={handleSave}
            className="text-[11px] uppercase tracking-wider px-4 py-1.5 rounded border border-wb text-wb hover:bg-wb hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving…" : isComputing ? "Computing…" : "Save"}
          </button>
        </div>
      </aside>
    </>
  );
}

function Chip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn";
}) {
  return (
    <span
      className={`px-2 py-1 rounded border text-[11px] ${
        tone === "ok"
          ? "border-border2 text-muted"
          : "border-warn/60 text-warn"
      }`}
    >
      <span className="text-dim mr-1.5">{label}:</span>
      {value}
    </span>
  );
}

interface ValidationOK {
  ok: true;
  errors: never[];
  body: JfConfigBody;
}
interface ValidationFail {
  ok: false;
  errors: string[];
  body?: undefined;
}
type ValidationResult = ValidationOK | ValidationFail;

function validate(
  levels: LevelRow[],
  soils: SoilRowDraft[],
  transitionWindowM: number | "",
  customStoppageHours: number | "",
  driveLengthM: number | null,
): ValidationResult {
  const errors: string[] = [];
  if (driveLengthM == null || driveLengthM <= 0) {
    errors.push("• Drive length must be set first.");
  }
  if (transitionWindowM === "" || transitionWindowM <= 0) {
    errors.push("• Transition window must be a positive number.");
  }
  if (
    customStoppageHours !== "" &&
    (customStoppageHours <= 0 || !Number.isFinite(customStoppageHours))
  ) {
    errors.push(
      "• Custom stoppage must be a positive number of hours, or blank.",
    );
  }
  if (levels.length < 2) {
    errors.push("• At least two level rows are required.");
  }
  const numLevels: { ch: number; g: number; w: number; t: number }[] = [];
  for (let i = 0; i < levels.length; i++) {
    const r = levels[i]!;
    if (
      r.chainageM === "" ||
      r.groundM === "" ||
      r.waterM === "" ||
      r.tunnelM === ""
    ) {
      errors.push(`• Level row ${i + 1} is missing values.`);
      continue;
    }
    numLevels.push({
      ch: Number(r.chainageM),
      g: Number(r.groundM),
      w: Number(r.waterM),
      t: Number(r.tunnelM),
    });
  }
  if (numLevels.length === levels.length) {
    for (let i = 1; i < numLevels.length; i++) {
      if (numLevels[i]!.ch <= numLevels[i - 1]!.ch) {
        errors.push(`• Level chainages must strictly increase (row ${i + 1}).`);
        break;
      }
    }
    if (numLevels.length > 0 && !APPROX(numLevels[0]!.ch, 0)) {
      errors.push("• First level row chainage must be 0.");
    }
    if (
      driveLengthM != null &&
      numLevels.length > 0 &&
      !APPROX(numLevels[numLevels.length - 1]!.ch, driveLengthM)
    ) {
      errors.push(`• Last level row chainage must equal ${driveLengthM} m.`);
    }
    // Geometry sanity: tunnel level must sit below ground level. The face
    // pressure formula goes negative when Zt > Zg, which is the most
    // common cause of "negative face force" in the pre-drive prediction.
    for (let i = 0; i < numLevels.length; i++) {
      if (numLevels[i]!.t >= numLevels[i]!.g) {
        errors.push(
          `• Level row ${i + 1}: tunnel (${numLevels[i]!.t}) must be below ground (${numLevels[i]!.g}). All levels are elevations on the same datum, not depths.`,
        );
        break;
      }
    }
  }

  const numSoils: {
    soilType: SoilRowDraft["soilType"];
    unitWeightKnm3: number;
    startM: number;
    endM: number;
    sfSsKpaInit: number;
    cInit: number;
    aInit: number;
  }[] = [];
  if (soils.length === 0) errors.push("• At least one soil layer is required.");
  for (let i = 0; i < soils.length; i++) {
    const s = soils[i]!;
    const description = s.soilType.trim();
    if (description.length === 0) {
      errors.push(`• Soil row ${i + 1} needs a description.`);
      continue;
    }
    if (
      s.unitWeightKnm3 === "" ||
      s.startM === "" ||
      s.endM === "" ||
      s.sfSsKpaInit === "" ||
      s.cInit === "" ||
      s.aInit === ""
    ) {
      errors.push(`• Soil row ${i + 1} is missing values.`);
      continue;
    }
    if (Number(s.endM) <= Number(s.startM)) {
      errors.push(`• Soil row ${i + 1} end must be greater than start.`);
      continue;
    }
    if ([s.unitWeightKnm3, s.sfSsKpaInit, s.cInit, s.aInit].some((v) => Number(v) < 0)) {
      errors.push(`• Soil row ${i + 1} coefficients must be non-negative.`);
      continue;
    }
    numSoils.push({
      soilType: description,
      unitWeightKnm3: Number(s.unitWeightKnm3),
      startM: Number(s.startM),
      endM: Number(s.endM),
      sfSsKpaInit: Number(s.sfSsKpaInit),
      cInit: Number(s.cInit),
      aInit: Number(s.aInit),
    });
  }
  if (numSoils.length === soils.length && numSoils.length > 0) {
    numSoils.sort((a, b) => a.startM - b.startM);
    if (!APPROX(numSoils[0]!.startM, 0)) {
      errors.push("• First soil layer must start at 0.");
    }
    if (
      driveLengthM != null &&
      !APPROX(numSoils[numSoils.length - 1]!.endM, driveLengthM)
    ) {
      errors.push(`• Last soil layer must end at ${driveLengthM} m.`);
    }
    for (let i = 1; i < numSoils.length; i++) {
      if (!APPROX(numSoils[i - 1]!.endM, numSoils[i]!.startM)) {
        errors.push(
          `• Gap between soil rows ${i} (ends ${numSoils[i - 1]!.endM}) and ${i + 1} (starts ${numSoils[i]!.startM}).`,
        );
        break;
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    errors: [] as never[],
    body: {
      transitionWindowM: Number(transitionWindowM),
      customStoppageHours:
        customStoppageHours === "" ? null : Number(customStoppageHours),
      levelChainagesM: numLevels.map((r) => r.ch),
      groundLevelsM: numLevels.map((r) => r.g),
      waterLevelsM: numLevels.map((r) => r.w),
      tunnelLevelsM: numLevels.map((r) => r.t),
      soils: numSoils.map((s) => ({
        soilType: s.soilType,
        unitWeightKnm3: s.unitWeightKnm3,
        startM: s.startM,
        endM: s.endM,
        sfSsKpaInit: s.sfSsKpaInit,
        cInit: s.cInit,
        aInit: s.aInit,
      })),
    },
  };
}

/**
 * Map a `JfSchematicSuggestion` from the auto-analyze endpoint into the
 * editor state. sf_ss / c / a are populated with placeholder constants
 * — they aren't readable from the drawing and the user is expected to
 * tune them before saving.
 */
function applyJfSchematic(
  schematic: JfSchematicSuggestion,
  setters: {
    setLevels: (rows: LevelRow[]) => void;
    setSoils: (rows: SoilRowDraft[]) => void;
  },
): void {
  if (schematic.levels.length >= 2) {
    setters.setLevels(
      schematic.levels.map((r) => ({
        chainageM: r.chainageM,
        groundM: r.groundM,
        waterM: r.waterM,
        tunnelM: r.tunnelM,
      })),
    );
  }
  if (schematic.soils.length > 0) {
    setters.setSoils(
      schematic.soils.map((s) => ({
        soilType: s.description,
        unitWeightKnm3: s.unitWeightKnm3,
        startM: s.startM,
        endM: s.endM,
        sfSsKpaInit: s.sfSsKpa ?? FALLBACK_SF_SS_KPA,
        cInit: s.c ?? FALLBACK_C,
        aInit: s.a ?? FALLBACK_A,
      })),
    );
  }
}
